'use client';
import { useSite, useT } from '../../lib/i18n';
import { pickName } from '../../lib/i18n-core';
import type { CategoryNode, CategoryTree, ListStyleBlock } from '../../lib/site';
import { Icon } from '../Icon';
import type { ListPrefs } from './listPrefs';
import { ListControls } from './ListControls';
import { typeLabel } from './labels';

/** 종류 탭 — 현행 `ptype_select`(전체·단과 L·패키지 P·프리패스 F) */
const TYPES = ['LECTURE', 'PACKAGE', 'FREEPASS'] as const;
/** 추천 배지 — 현행 라디오 BEST·EVENT·HIT·NEW */
const BADGES = ['BEST', 'EVENT', 'HIT', 'NEW'] as const;
export interface FilterState {
  type: string | null;
  /** 단계별로 고른 분류 — `[1단계, 2단계, 3단계]`, 안 고른 칸은 null */
  categoryPath: (number | null)[];
  badge: string | null;
  q: string;
}

/**
 * 필터줄 — 가이드 `tab-primary` · `cat-filter` · `bbs-search-bar` · `sort-tabs` · `view-toggle`.
 *
 * **무엇을 그리는지는 설정이 정한다**(§④-a).
 *  - 종류 탭    : `listStyle.typeTab`(현행 `type: lecture|book`)
 *  - 분류 필터  : `listStyle.categoryTab`(미사용/전체보기 포함/미포함) × `categoryDepth` 줄 수
 *  - 목록 전환 : `listStyle.viewToggle` · 정렬 : `listStyle.sortControls`
 *    (현행은 `list_option` 한 칸이었는데 2026-08-23 에 둘로 나눴다)
 * 검색은 설정과 무관하게 늘 있다.
 */
export function CourseFilters({ style, categories, filters, prefs, onFilters, onPrefs, searchPlaceholder, controlsLeft }: {
  style: ListStyleBlock;
  categories: CategoryTree | null;
  filters: FilterState;
  prefs: ListPrefs;
  onFilters: (next: FilterState) => void;
  onPrefs: (next: ListPrefs) => void;
  searchPlaceholder: string;
  /** 정렬 줄 왼쪽에 끼울 것 — 구독 보기 토글 */
  controlsLeft?: React.ReactNode;
}) {
  const t = useT();
  const { lang } = useSite();

  /** 분류 한 단계 고름 — 아래 단계 선택은 지운다(상위가 바뀌면 하위는 뜻을 잃는다) */
  const pickCategory = (depth: number, id: number | null) => {
    const next = filters.categoryPath.slice();
    next[depth - 1] = id;
    for (let i = depth; i < next.length; i++) next[i] = null;
    onFilters({ ...filters, categoryPath: next });
  };

  // 그릴 분류 줄 — 상위에서 고른 것의 자식만 내려간다
  const rows: CategoryNode[][] = [];
  if (categories && style.categoryTab !== 'none') {
    let level = categories.tree;
    for (let d = 1; d <= categories.depthLimit; d++) {
      if (!level.length) break;
      rows.push(level);
      const picked = filters.categoryPath[d - 1];
      level = picked ? (level.find((n) => n.id === picked)?.children ?? []) : [];
    }
  }

  return (
    <>
      {/* 종류 탭 */}
      {style.typeTab && (
        <div className="tab-primary mb15">
          <button className={`tab-primary-btn${filters.type == null ? ' active' : ''}`}
            onClick={() => onFilters({ ...filters, type: null })}>{t('prd.all')}</button>
          {TYPES.map((ty) => (
            <button key={ty} className={`tab-primary-btn${filters.type === ty ? ' active' : ''}`}
              onClick={() => onFilters({ ...filters, type: ty })}>{typeLabel(ty, t)}</button>
          ))}
        </div>
      )}

      {/* 분류 뎁스 필터 */}
      {rows.length > 0 && (
        <div className="cat-filter mb15">
          {rows.map((nodes, i) => {
            const depth = i + 1;
            const picked = filters.categoryPath[depth - 1] ?? null;
            return (
              <div className="cat-filter-row" key={depth}>
                {/* '전체' 칸은 설정이 정한다 — 현행 curri_select 1=포함 / 2=미포함 */}
                {style.categoryTab === 'withAll' && (
                  <button className={`cat-chip${picked == null ? ' active' : ''}`}
                    onClick={() => pickCategory(depth, null)}>{t('prd.all')}</button>
                )}
                {nodes.map((n) => (
                  <button key={n.id} className={`cat-chip${picked === n.id ? ' active' : ''}`}
                    onClick={() => pickCategory(depth, picked === n.id ? null : n.id)}>
                    {pickName(n.name, lang)}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* 검색 + 추천 배지 */}
      <div className="bbs-search-bar mb15">
        <div className="bbs-search-inner">
          <select className="form-select bbs-search-type" value={filters.badge ?? ''}
            onChange={(e) => onFilters({ ...filters, badge: e.target.value || null })}>
            <option value="">{t('prd.badgeAll')}</option>
            {BADGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <div className="bbs-search-input-wrap">
            <Icon name="search" />
            <input type="text" className="bbs-search-input" placeholder={searchPlaceholder}
              defaultValue={filters.q}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onFilters({ ...filters, q: (e.target as HTMLInputElement).value });
              }}
              onBlur={(e) => { if (e.target.value !== filters.q) onFilters({ ...filters, q: e.target.value }); }} />
          </div>
          <button className="action-btn action-submit btn-sm bbs-search-btn"
            onClick={(e) => {
              const input = (e.currentTarget.closest('.bbs-search-inner')?.querySelector('.bbs-search-input') as HTMLInputElement | null);
              onFilters({ ...filters, q: input?.value ?? '' });
            }}>{t('prd.search')}</button>
        </div>
      </div>

      {/* 정렬 · 목록 전환 — 공용 조각(ListControls). 첫 화면·프로모션 묶음도 같은 것을 쓴다 */}
      <ListControls prefs={prefs} onPrefs={onPrefs} left={controlsLeft}
        viewToggle={style.viewToggle} sortControls={style.sortControls} />
    </>
  );
}
